/**
 * Overview group edit + settings modals (FE-5d) — extracted from overview.route.js.
 */

import { DEFAULT_REMOTE_SIREN_MS } from "./constants.js";

/**
 * @param {Element | null | undefined} btn
 * @returns {{ metaKey: string, groupKey: string, tenantOwner: string }}
 */
export function readOverviewGroupSlotFromBtn(btn) {
  const card = btn && btn.closest ? btn.closest(".js-group-card") : null;
  const metaKey = String((btn && btn.dataset && btn.dataset.metaKey) || (card && card.getAttribute("data-meta-key")) || "");
  const groupKey = String((btn && btn.dataset && btn.dataset.group) || (card && card.getAttribute("data-group")) || "");
  const tenantOwner = String((btn && btn.dataset && btn.dataset.owner) || (card && card.getAttribute("data-owner")) || "");
  return { metaKey, groupKey, tenantOwner };
}

/** @param {unknown} err */
function isControlDbBusyError(err) {
  const msg = String((err && /** @type {{ message?: string }} */ (err).message) || err || "");
  return (
    msg.startsWith("503")
    || msg.includes("CONTROL_DB_BUSY")
    || /system is busy|database contended|sqlite_write_lock/i.test(msg)
  );
}

/**
 * Bulk set notification_group; on CONTROL_DB_BUSY fall back to per-device PATCH.
 * @param {object} deps
 * @param {string[]} deviceIds
 * @param {string} notificationGroup
 * @param {{ skipAclBlocked?: boolean }} [opts]
 */
async function applyNotificationGroupMembership(deps, deviceIds, notificationGroup, opts = {}) {
  const {
    api,
    toast,
    buildPostDevicesBulkProfilePath,
    buildPatchDeviceProfilePath,
  } = deps;
  const list = (Array.isArray(deviceIds) ? deviceIds : []).map((x) => String(x || "").trim()).filter(Boolean);
  if (!list.length) return { acl_blocked_skipped: [], changed_group_rows: 0 };
  const skipAcl = opts.skipAclBlocked !== false && !!String(notificationGroup || "").trim();
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  if (typeof buildPostDevicesBulkProfilePath === "function") {
    try {
      const res = await api(buildPostDevicesBulkProfilePath(), {
        method: "POST",
        body: {
          device_ids: list,
          set_notification_group: true,
          notification_group: notificationGroup,
          skip_acl_blocked: skipAcl,
        },
      });
      return res && typeof res === "object" ? res : { acl_blocked_skipped: [], changed_group_rows: list.length };
    } catch (e) {
      if (!isControlDbBusyError(e)) throw e;
      toast("Server busy — updating boards one by one…", "warn");
    }
  }

  if (typeof buildPatchDeviceProfilePath !== "function") {
    throw new Error("503 System is busy. Wait 10–30 seconds and try Save again.");
  }
  let ok = 0;
  let fail = 0;
  for (const id of list) {
    let done = false;
    for (let attempt = 1; attempt <= 4 && !done; attempt++) {
      try {
        await api(buildPatchDeviceProfilePath(id), {
          method: "PATCH",
          body: { notification_group: notificationGroup },
        });
        ok += 1;
        done = true;
      } catch (e) {
        if (isControlDbBusyError(e) && attempt < 4) {
          await sleep(700 * attempt);
          continue;
        }
        fail += 1;
        done = true;
      }
    }
    await sleep(100);
  }
  if (fail && !ok) {
    throw new Error("503 System is busy. Wait 10–30 seconds and try Save again.");
  }
  if (fail) toast(`${ok} board(s) updated, ${fail} still busy — retry those shortly`, "warn");
  return { acl_blocked_skipped: [], changed_group_rows: ok };
}

/**
 * @param {object} ctx
 */
export function createOverviewGroupModalsController(ctx) {
  const {
    view,
    grpModalEl,
    grpSetModalEl,
    api,
    getMe,
    canFn,
    toast,
    escapeHtml,
    $,
    $$,
    setChildMarkup,
    getDevices,
    devicesForGroups,
    inferUniqueOwnerForGroup,
    getById,
    getMeta,
    saveGroupMeta,
    loadLocalGroupSettings,
    saveLocalGroupSettings,
    getGroupSettingsMap,
    groupApiCaps,
    saveGroupApiCaps,
    groupDelayTimers,
    groupCardMetaKey,
    canonicalGroupKey,
    displayGroupName,
    parseGroupMetaKey,
    groupDeviceRow,
    groupDeviceIdsFromList,
    groupDeviceIdsFromSlot,
    groupSharedBySlot,
    groupSharedByNotificationKey,
    sirenTargetIdsForSlot,
    canGroupSirenForSlot,
    tryContractApiCall,
    tryGroupDeleteContractCall,
    putGroupCardSettingsV1,
    applyGroupCardSettingsV1,
    deleteGroupCardV1,
    buildPostGroupCardsApplyPath,
    buildPatchDeviceProfilePath,
    buildPostDevicesBulkProfilePath,
    buildPostAdminNotificationGroupShareLabelPath,
    buildPostAlertsPath,
    buildBulkAlertBody,
    buildPostDeviceSelfTestPath,
    buildPostDeviceCommandsPath,
    runGroupApplyOnAction,
    runGroupDeleteAction,
    bustDeviceListCaches,
    onGroupsChanged,
  } = ctx;

  let editingGroup = "";
  const editingSettingsSlot = { metaKey: "", groupKey: "", tenantOwner: "" };

  const persistSettingsLocal = (metaKey, payload) => {
    const all = loadLocalGroupSettings();
    all[metaKey] = Object.assign({}, payload || {});
    saveLocalGroupSettings(all);
  };

  const saveGroupSettingsCompat = async (groupKey, tenantOwner, payload) => {
    const mk = groupCardMetaKey(groupKey, tenantOwner);
    const body = Object.assign({}, payload || {});
    const me = getMe();
    if (me && me.role === "superadmin" && String(tenantOwner || "").trim()) {
      body.owner_admin = String(tenantOwner).trim();
    }
    if (!groupApiCaps.settings) {
      persistSettingsLocal(mk, body);
      return body;
    }
    try {
      return await putGroupCardSettingsV1({
        group_key: groupKey,
        owner_admin: tenantOwner || undefined,
        body,
        timeoutMs: 12000,
      });
    } catch (e) {
      const msg = String((e && e.message) || e || "");
      if (msg.includes("404") || msg.includes("405") || msg.includes("501")) {
        groupApiCaps.settings = false;
        saveGroupApiCaps(groupApiCaps);
        persistSettingsLocal(mk, body);
        return body;
      }
      throw e;
    }
  };

  const tryApplyGroupV1 = (gk, oa) => applyGroupCardSettingsV1({
    group_key: gk,
    owner_admin: oa || undefined,
    timeoutMs: 45000,
  });

  const applyGroupSettingsFallback = async (groupKey, tenantOwner, payload) => {
    const slot = { metaKey: groupCardMetaKey(groupKey, tenantOwner), groupKey, tenantOwner: tenantOwner || "" };
    const ids = sirenTargetIdsForSlot(slot);
    if (!ids.length) throw new Error("No boards in this group you may alarm");
    if (!canGroupSirenForSlot(slot)) throw new Error("No alarm permission on any board in this group");
    const durationMs = Number(payload.trigger_duration_ms || DEFAULT_REMOTE_SIREN_MS);
    const timerKey = slot.metaKey;
    const prevTimer = groupDelayTimers.get(timerKey);
    if (prevTimer) {
      clearTimeout(prevTimer);
      groupDelayTimers.delete(timerKey);
    }
    await api(buildPostAlertsPath(), {
      method: "POST",
      body: buildBulkAlertBody({
        action: "on",
        durationMs,
        notificationGroup: groupKey,
        ownerAdmin: tenantOwner,
        deviceIds: ids,
      }),
    });
    let rebootJobs = 0;
    let selfTests = 0;
    if (payload.reboot_self_check) {
      if (!canFn("can_send_command")) throw new Error("Reboot + self-check needs command permission");
      for (const did of ids) {
        await api(buildPostDeviceSelfTestPath(did), { method: "POST" });
        selfTests += 1;
        await api(buildPostDeviceCommandsPath(did), {
          method: "POST",
          body: { cmd: "reboot", params: {} },
        });
        rebootJobs += 1;
      }
    }
    return { ok: true, fallback: true, device_count: ids.length, self_tests: selfTests, reboot_jobs: rebootJobs };
  };

  const deleteGroupCard = async (groupKey, tenantOwner) => runGroupDeleteAction({
    groupKey,
    ownerAdmin: tenantOwner,
    apiCaps: groupApiCaps,
    saveApiCaps: saveGroupApiCaps,
    tryDeleteV1: (gk, oa) => deleteGroupCardV1({
      group_key: gk,
      owner_admin: oa || undefined,
      timeoutMs: 20000,
    }),
    tryDeletePostRoute: (gk, oa) => tryGroupDeleteContractCall("post", gk, oa),
    tryDeleteRoute: (gk, oa) => tryGroupDeleteContractCall("delete", gk, oa),
  });

  const collectSettingsPayload = () => {
    const durMinEl = $("#gsDurMin", view);
    const durMin = parseFloat((durMinEl && durMinEl.value) || "3", 10);
    const delay = parseInt($("#gsDelay", view).value, 10);
    const reboot = !!$("#gsReboot", view).checked;
    if (!Number.isFinite(durMin) || durMin < 0.5 || durMin > 5) {
      throw new Error("Siren duration must be 0.5–5 minutes");
    }
    if (!Number.isFinite(delay) || delay < 0 || delay > 3600) {
      throw new Error("Delay seconds must be 0-3600");
    }
    return {
      trigger_mode: delay > 0 ? "delay" : "continuous",
      trigger_duration_ms: Math.round(durMin * 60000),
      delay_seconds: delay,
      reboot_self_check: reboot,
    };
  };

  const closeSettingsModal = () => {
    grpSetModalEl.style.display = "none";
    grpSetModalEl.classList.remove("is-open");
    grpSetModalEl.hidden = true;
    grpSetModalEl.setAttribute("aria-hidden", "true");
  };

  const openSettingsModal = (slot) => {
    editingSettingsSlot.metaKey = slot.metaKey || "";
    editingSettingsSlot.groupKey = slot.groupKey || "";
    editingSettingsSlot.tenantOwner = slot.tenantOwner || "";
    if (!editingSettingsSlot.metaKey) return;
    const groupSettingsMap = getGroupSettingsMap();
    const gs = groupSettingsMap.get(editingSettingsSlot.metaKey) || {
      trigger_mode: "continuous",
      trigger_duration_ms: DEFAULT_REMOTE_SIREN_MS,
      delay_seconds: 0,
      reboot_self_check: false,
    };
    const label = editingSettingsSlot.tenantOwner
      ? `Group: ${editingSettingsSlot.groupKey} · admin: ${editingSettingsSlot.tenantOwner}`
      : `Group: ${editingSettingsSlot.groupKey}`;
    const byId = getById();
    const rowsG = groupDeviceIdsFromSlot(slot).map((id) => byId.get(String(id))).filter(Boolean);
    const syncNote = rowsG.some((d) => d && d.is_shared)
      ? `<br/><span class="muted" style="font-size:12px">Saves apply to the owner’s group alarm policy (all boards here must grant you Operate access).</span>`
      : "";
    const labEl = $("#gsKeyLabel", view);
    if (labEl) labEl.innerHTML = `${escapeHtml(label)}${syncNote}`;
    const durMin = Math.max(0.5, Math.min(5, (Number(gs.trigger_duration_ms) || DEFAULT_REMOTE_SIREN_MS) / 60000));
    const gdm = $("#gsDurMin", view);
    if (gdm) gdm.value = String(Math.round(durMin * 10) / 10);
    $("#gsDelay", view).value = String(Number(gs.delay_seconds || 0));
    $("#gsReboot", view).checked = !!gs.reboot_self_check;
    grpSetModalEl.hidden = false;
    grpSetModalEl.removeAttribute("aria-hidden");
    grpSetModalEl.classList.add("is-open");
    grpSetModalEl.style.display = "flex";
  };

  const renderGroupModalDevicePickList = () => {
    const pick = $("#gmDevices", view);
    if (!pick) return;
    const meta = getMeta();
    const parsed = parseGroupMetaKey(editingGroup);
    const gkFromMeta = canonicalGroupKey(parsed.groupKey || "");
    const gkFieldEl = $("#gmKey", view);
    const gkFromField = canonicalGroupKey((gkFieldEl && gkFieldEl.value) || "");
    const contextKey = gkFromMeta || gkFromField;
    const m = meta[editingGroup] || { display_name: "", owner_name: "", phone: "", email: "", device_ids: [] };
    const sel = new Set((m.device_ids || []).map(String));
    const slot = {
      metaKey: editingGroup,
      groupKey: parsed.groupKey || contextKey,
      tenantOwner: parsed.tenantOwner,
    };
    const isSharedGroup = groupSharedBySlot(slot).length > 0;
    const body = devicesForGroups()
      .map((d) => {
        const did = String(d && d.device_id != null ? d.device_id : "").trim();
        if (!did) return "";
        const ctxKey = canonicalGroupKey(contextKey || "");
        const ngCanon = canonicalGroupKey(String((d && d.notification_group) || "").trim());
        const inThis = !!ctxKey && ngCanon === ctxKey;
        const foreignOccupied = !!(ngCanon && (!ctxKey || ngCanon !== ctxKey));
        const checked = inThis || (sel.has(did) && !foreignOccupied);
        const rowDisabled = isSharedGroup || foreignOccupied;
        const lockInGroup = inThis && rowDisabled;
        const onCardRemovable = inThis && !isSharedGroup && !foreignOccupied;
        return groupDeviceRow(d, {
          checked,
          disabled: rowDisabled,
          lockInGroup,
          contextKey,
          foreignOccupied,
          onCardRemovable,
        });
      })
      .filter(Boolean)
      .join("");
    const shell = isSharedGroup
      ? `<p class="grp-device-picker__readonly muted">Shared group — board membership is read-only.</p><div class="grp-device-picker__list">${body}</div>`
      : `<div class="grp-device-picker__toolbar grp-device-picker__toolbar--compact">
          <div class="grp-device-picker__chips" aria-label="Legend">
            <span class="grp-device-pill grp-device-pill--this">On card</span>
            <span class="grp-device-pill grp-device-pill--other">Other</span>
            <span class="grp-device-pill grp-device-pill--neutral">None</span>
          </div>
        </div>
        <div class="grp-device-picker__headrow muted" aria-hidden="true">
          <span></span><span>Board</span><span class="grp-device-picker__head-status">Tag</span>
        </div>
        <div class="grp-device-picker__list">${body}</div>`;
    setChildMarkup(pick, shell);
  };

  const closeGroupModal = () => {
    grpModalEl.style.display = "none";
    grpModalEl.classList.remove("is-open");
    grpModalEl.hidden = true;
    grpModalEl.setAttribute("aria-hidden", "true");
  };

  const openGroupModal = (metaKey) => {
    editingGroup = metaKey || "";
    const meta = getMeta();
    const parsed = parseGroupMetaKey(editingGroup);
    const gk = parsed.groupKey || "";
    const m = meta[editingGroup] || { display_name: gk || "", owner_name: "", phone: "", email: "", device_ids: [] };
    const keyEl = $("#gmKey", view);
    const nameEl = $("#gmName", view);
    const ownerEl = $("#gmOwner", view);
    const phoneEl = $("#gmPhone", view);
    const emailEl = $("#gmEmail", view);
    if (keyEl) keyEl.value = canonicalGroupKey(gk) || "";
    if (nameEl) nameEl.value = m.display_name || "";
    if (ownerEl) ownerEl.value = m.owner_name || "";
    if (phoneEl) phoneEl.value = m.phone || "";
    if (emailEl) emailEl.value = m.email || "";
    if (!grpModalEl.dataset.gmPickDelegated) {
      grpModalEl.dataset.gmPickDelegated = "1";
      grpModalEl.addEventListener("input", (ev) => {
        if (ev.target && ev.target.id === "gmKey" && grpModalEl.classList.contains("is-open")) {
          try { renderGroupModalDevicePickList(); } catch (_) {}
        }
      });
    }
    // Show shell first — device picker render must not block the open (click felt dead).
    grpModalEl.hidden = false;
    grpModalEl.removeAttribute("aria-hidden");
    grpModalEl.classList.add("is-open");
    grpModalEl.style.display = "flex";
    try {
      renderGroupModalDevicePickList();
    } catch (e) {
      console.warn("[group-modal] device picker render failed", e);
    }
  };

  async function saveGroupModal() {
    const me = getMe();
    const meta = getMeta();
    const byId = getById();
    const keyRaw = displayGroupName($("#gmKey", view).value || "");
    const key = canonicalGroupKey(keyRaw);
    if (!key) { toast("Group name is required", "err"); return; }
    const oldMetaKey = String(editingGroup || "").trim();
    const oldParsed = parseGroupMetaKey(oldMetaKey);
    const oldEntry = oldMetaKey && Object.prototype.hasOwnProperty.call(meta, oldMetaKey) ? meta[oldMetaKey] : null;
    let display_name = displayGroupName($("#gmName", view).value || "");
    if (!display_name) display_name = keyRaw || key;
    const owner_name = String($("#gmOwner", view).value || "").trim();
    const phone = String($("#gmPhone", view).value || "").trim();
    const email = String($("#gmEmail", view).value || "").trim();
    const picks = Array.from($$("#gmDevices input[type='checkbox']", view))
      .filter((x) => x.checked)
      .map((x) => String(x.value || "").trim())
      .filter(Boolean);
    let tenantForMeta = oldParsed.tenantOwner || "";
    if (me && me.role === "superadmin") {
      for (const id of picks) {
        const dev = byId.get(String(id));
        const o = dev && String(dev.owner_admin || "").trim();
        if (o) { tenantForMeta = o; break; }
      }
      if (!tenantForMeta) {
        tenantForMeta = inferUniqueOwnerForGroup(getDevices(), key, canonicalGroupKey);
      }
    }
    const newMetaKey = groupCardMetaKey(key, tenantForMeta);
    const groupLabelForShares = display_name || keyRaw || key;
    const syncShareLabel = async () => {
      const body = { group_display: key, group_label: groupLabelForShares };
      if (me && me.role === "superadmin" && String(tenantForMeta || "").trim()) {
        body.owner_admin = String(tenantForMeta).trim();
      }
      await api(buildPostAdminNotificationGroupShareLabelPath(), { method: "POST", body });
    };
    if (groupSharedByNotificationKey(key).length > 0) {
      try {
        await syncShareLabel();
      } catch (e) {
        console.warn("[group-share-label] sync skipped", e);
      }
      const keepIds = (oldEntry && Array.isArray(oldEntry.device_ids)) ? oldEntry.device_ids.map((x) => String(x)) : [];
      if (oldMetaKey && oldMetaKey !== newMetaKey && meta[oldMetaKey]) delete meta[oldMetaKey];
      meta[newMetaKey] = { display_name, owner_name, phone, email, device_ids: keepIds };
      saveGroupMeta(meta);
      try { bustDeviceListCaches(); } catch (_) {}
      closeGroupModal();
      onGroupsChanged();
      toast("Group card updated (shared group — board list is owner-managed)", "ok");
      return;
    }
    const previousDeviceIds = oldMetaKey ? groupDeviceIdsFromList(oldParsed.groupKey, oldParsed.tenantOwner) : [];
    if (picks.length === 0 && previousDeviceIds.length > 0) {
      if (!confirm("No boards selected — this clears the notification group for every board currently on this card. Continue?")) {
        return;
      }
    }
    let blockedSkipped = [];
    let blockedSavedCount = 0;
    const membershipDeps = {
      api,
      toast,
      buildPostDevicesBulkProfilePath,
      buildPatchDeviceProfilePath,
    };
    if (picks.length > 0) {
      try {
        const addRes = await applyNotificationGroupMembership(membershipDeps, picks, key, { skipAclBlocked: true });
        blockedSkipped = Array.isArray(addRes && addRes.acl_blocked_skipped)
          ? addRes.acl_blocked_skipped.map((x) => String(x || "").trim()).filter(Boolean)
          : [];
        blockedSavedCount = Number((addRes && addRes.changed_group_rows) || 0);
        const nextSet = new Set(picks);
        const removeIds = previousDeviceIds.filter((id) => !nextSet.has(id));
        if (removeIds.length > 0) {
          await applyNotificationGroupMembership(membershipDeps, removeIds, "", { skipAclBlocked: false });
        }
      } catch (e) {
        toast(isControlDbBusyError(e)
          ? "System is busy — wait 10–30 seconds and try Save again."
          : (e.message || e), "err");
        return;
      }
    } else {
      const removeIds = previousDeviceIds.filter(Boolean);
      if (removeIds.length > 0) {
        try {
          await applyNotificationGroupMembership(membershipDeps, removeIds, "", { skipAclBlocked: false });
        } catch (e) {
          toast(isControlDbBusyError(e)
            ? "System is busy — wait 10–30 seconds and try Save again."
            : (e.message || e), "err");
          return;
        }
      }
    }
    if (oldMetaKey && oldMetaKey !== newMetaKey && meta[oldMetaKey]) delete meta[oldMetaKey];
    try {
      await syncShareLabel();
    } catch (e) {
      console.warn("[group-share-label] sync skipped", e);
    }
    const finalDeviceIds = blockedSkipped.length
      ? picks.filter((id) => !blockedSkipped.includes(String(id)))
      : picks;
    meta[newMetaKey] = { display_name, owner_name, phone, email, device_ids: finalDeviceIds };
    saveGroupMeta(meta);
    try { bustDeviceListCaches(); } catch (_) {}
    closeGroupModal();
    onGroupsChanged();
    if (blockedSkipped.length > 0) {
      const sample = blockedSkipped.slice(0, 3).join(", ");
      const more = blockedSkipped.length > 3 ? "…" : "";
      const savedN = blockedSavedCount || finalDeviceIds.length;
      toast(
        `Group saved (${savedN}). Skipped ${blockedSkipped.length} board(s) with active per-board share — revoke on Shares page first: ${sample}${more}`,
        "warn",
      );
    } else {
      toast("Group saved — sibling alarm sharing updated for boards in this group", "ok");
    }
  }

  function wireHandlers() {
    $("#gsCancel", view).addEventListener("click", closeSettingsModal);
    $("#gsSave", view).addEventListener("click", async () => {
      try {
        if (!editingSettingsSlot.metaKey) throw new Error("No group selected");
        const payload = collectSettingsPayload();
        const r = await saveGroupSettingsCompat(
          editingSettingsSlot.groupKey,
          editingSettingsSlot.tenantOwner,
          payload,
        );
        getGroupSettingsMap().set(editingSettingsSlot.metaKey, r || payload);
        onGroupsChanged();
        closeSettingsModal();
        toast("Group settings saved", "ok");
      } catch (e) {
        toast(e.message || e, "err");
      }
    });
    $("#gsApply", view).addEventListener("click", async () => {
      try {
        if (!editingSettingsSlot.metaKey) throw new Error("No group selected");
        const payload = collectSettingsPayload();
        await saveGroupSettingsCompat(
          editingSettingsSlot.groupKey,
          editingSettingsSlot.tenantOwner,
          payload,
        );
        getGroupSettingsMap().set(editingSettingsSlot.metaKey, payload);
        const r = await runGroupApplyOnAction({
          groupKey: editingSettingsSlot.groupKey,
          ownerAdmin: editingSettingsSlot.tenantOwner,
          payload,
          apiCaps: groupApiCaps,
          saveApiCaps: saveGroupApiCaps,
          tryApplyV1: tryApplyGroupV1,
          tryApplyRoute: (gk, oa) => tryContractApiCall(
            buildPostGroupCardsApplyPath(gk, oa),
            { method: "POST" },
          ),
          applyFallback: applyGroupSettingsFallback,
        });
        onGroupsChanged();
        closeSettingsModal();
        toast(`Applied to ${Number(r.device_count || 0)} boards${r && r.fallback ? " (fallback mode)" : ""}`, "ok");
      } catch (e) {
        toast(e.message || e, "err");
      }
    });
    $("#gmCancel", view).addEventListener("click", closeGroupModal);
    $("#gmSave", view).addEventListener("click", () => saveGroupModal());
  }

  return {
    openGroupModal,
    openSettingsModal,
    deleteGroupCard,
    applyGroupSettingsFallback,
    tryApplyGroupV1,
    wireHandlers,
  };
}
